import React, { useState, useEffect } from "react";
import { Card } from 'react-bootstrap';
import GamePageAd from "../ad/gamePageAd";
import GameTags from "../gameTag/gameTag";
import { useLocation, useParams } from 'react-router-dom';
import GameCard from "../gamecard/gameCard";
import Loader from "../Loader/loader";
import AllGames from "../../data/gameData.json";
import { Expand, Shrink } from 'lucide-react';
import { Helmet } from 'react-helmet-async';


const Game = () => {
  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState({});
  const [similarGames, setsimilarGames] = useState([]);
  const [selectedGameUrl, setSelectedGameUrl] = useState('');
  const [iframeloading, setIFrameLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mobileFullPage, setMobileFullPage] = useState(false);

  const { gameId } = useParams();
  const location = useLocation();
  const { game } = location.state || {};

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSelectedGameUrl('');
    setMobileFullPage(false);
    if (!game) {
      fetchGameDetails(gameId);
    } else {
      if (typeof game.tags === 'string') {
        game.tags = game.tags.split(",");
      }
      fetchSimilarGames(game.category);
      setGameData(game);
      setLoading(false);
    }
  }, [game, gameId]);

  useEffect(() => {
    if (!(gameData && gameData.url)) return;

    const removeExistingElements = () => {
      const oldScript = document.getElementById("gamemonetize-video-api");
      if (oldScript) oldScript.remove();
      const videoContainer = document.getElementById("gamemonetize-video");
      if (videoContainer) videoContainer.innerHTML = "";
    };

    const loadJQuery = () => new Promise((resolve) => {
      if (!window.jQuery) {
        const jQueryScript = document.createElement("script");
        jQueryScript.src = "https://code.jquery.com/jquery-3.6.0.min.js";
        jQueryScript.onload = resolve;
        document.head.appendChild(jQueryScript);
      } else {
        resolve();
      }
    });

    const loadVideoScript = () => {
      const script = document.createElement("script");
      script.id = "gamemonetize-video-api";
      script.src = `https://api.gamemonetize.com/video.js?v=${Date.now()}`;
      script.async = true;
      document.body.appendChild(script);
    };

    const setupVideo = async () => {
      const idParts = gameData.url.split("/");
      const gameId = idParts[idParts.length - 2];
      window.VIDEO_OPTIONS = {
        gameid: gameId,
        width: "100%",
        height: "480px",
        color: "#3f007e"
      };

      removeExistingElements();
      await loadJQuery();
      loadVideoScript();
    };

    setupVideo();

    return () => {
      removeExistingElements();
    };
  }, [gameData]);

  useEffect(() => {
    if (mobileFullPage) {
      document.body.classList.add("mobile-game-fullscreen");
    } else {
      document.body.classList.remove("mobile-game-fullscreen");
    }
  }, [mobileFullPage]);

  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsElement = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(!!fsElement);
      if (!fsElement) setMobileFullPage(false);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    const iframeContainer = document.getElementById("iframe-container");
    if (!iframeContainer) return;

    if (window.innerWidth < 768) {
      setMobileFullPage(true);
    }
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      const request = iframeContainer.requestFullscreen || iframeContainer.webkitRequestFullscreen;
      request?.call(iframeContainer);
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      exit?.call(document).catch(() => {
        setMobileFullPage(false);
      });
    }
  };

  const handleGameonClick = (url) => () => {
    setSelectedGameUrl(url);
    setIFrameLoading(true);
    if (window.innerWidth < 768) {
      setMobileFullPage(true);
    }
  };

  const fetchGameDetails = async (id) => {
    try {
      const allGamesData = AllGames.data;
      const game = allGamesData.find(g => g.slug === id);
      if (!game) {
        fetchGameDetails("jumpycat");
      }
      if (typeof game?.tags === 'string') {
        game.tags = game.tags.split(",");
      }
      setGameData(game);
      fetchSimilarGames(game?.category);
      setLoading(false);
    } catch (error) {
      fetchGameDetails("jumpycat");
      console.error("Failed to fetch data:", error);
    }
  };

  const fetchSimilarGames = async (category) => {
    let categoryDetails = AllGames.data.filter(cat => cat.category === category);
    categoryDetails = categoryDetails.slice(0, 12)
    setsimilarGames(categoryDetails);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#121212] text-[#00F0FF]">
        <Loader />
      </div>
    );
  }

  return (
    <div className={`max-w-6xl mx-auto ${mobileFullPage ? 'p-0' : 'px-4 sm:px-6 py-8'} bg-[#0d0d1a] text-[#00F0FF] flex flex-col items-center`}>
      <Helmet>
        <title>{gameData?.title} | BrowserArena</title>
        <meta name="description" content={`Play ${gameData?.title} for free online. ${gameData?.description?.slice(0, 140)}...`} />
      </Helmet>

      {iframeloading && <div className="mb-4"><Loader /></div>}

      {!selectedGameUrl && (
        <Card
          className="relative game-card cursor-pointer mx-auto w-full max-w-md shadow-xl"
          onClick={handleGameonClick(gameData.url)}
        >
          <Card.Img
            variant="top"
            src={gameData.thumb}
            className="w-full object-cover rounded-lg"
            loading="eager"
            fetchpriority="high"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <button className="bg-[#00F0FF] text-[#0d0d1a] px-6 py-2 text-lg font-bold rounded-full shadow-lg hover:bg-cyan-400 transition">
              ▶ Play
            </button>
          </div>
        </Card>
      )}

      {selectedGameUrl && (
        <div
          id="iframe-container"
          className={`w-full ${mobileFullPage ? 'fixed top-0 left-0 right-0 bottom-0 z-50 bg-black flex items-center justify-center' : 'max-w-3xl relative border border-[#00F0FF] rounded-lg p-2 mt-4'}`}
        >
          {mobileFullPage && (
            <button
              onClick={() => setMobileFullPage(false)}
              className="absolute top-4 right-4 bg-[#FF007F] text-white px-4 py-2 rounded-full shadow-lg z-50"
            >
              ✖ Exit
            </button>
          )}

          {!mobileFullPage && (
            <button
              onClick={toggleFullscreen}
              className="absolute bottom-3 right-3 bg-[#00F0FF] text-[#0d0d1a] p-2 rounded-full shadow-lg hover:bg-cyan-400 transition z-10"
            >
              {isFullscreen ? <Shrink size={20} /> : <Expand size={20} />}
            </button>
          )}

          <iframe
            key={`${selectedGameUrl}-${mobileFullPage ? 'full' : 'normal'}`}
            src={selectedGameUrl}
            title={gameData.title}
            width="100%"
            height={mobileFullPage ? "100%" : "500px"}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            onLoad={() => setIFrameLoading(false)}
            className={`rounded-md ${mobileFullPage ? 'h-[calc(var(--vh)_*_100)] min-h-[100dvh] w-full' : ''}`}
          />
        </div>
      )}

      {!mobileFullPage && (
        <>
          <div className="text-center mt-8 px-2 sm:px-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#00F0FF] mb-4">{gameData.title}</h1>
            <GameTags tags={gameData.tags} />
            <p className="mt-2 text-sm sm:text-base">🎮 <strong>Instructions:</strong> {gameData.instructions}</p>
            <p className="mt-2 text-sm sm:text-base">📜 <strong>Description:</strong> {gameData.description}</p>
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-[#00F0FF] mt-8">🎥 Walkthrough</h2>
          <div id="gamemonetize-video" className="w-full mt-4 px-2" />

          <h2 className="text-xl sm:text-2xl font-bold text-[#00F0FF] mt-10 mb-4 text-center">🕹️ Similar Games</h2>

          <div className="grid grid-cols-3 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 md:gap-5">
            {similarGames.map((game, index) =>
              game.id !== gameData.id && (
                game.title === "ad"
                  ? <GamePageAd key={index} />
                  : <GameCard key={index} game={game} />
              )
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Game;
